import { auth } from '../../../firebase';

const API_BASE = 'https://bluebirddocumentationadmin.pythonanywhere.com/notebooks';

/**
 * Get auth headers for API requests
 */
const getAuthHeaders = async () => {
    const token = await auth.currentUser?.getIdToken();
    const uid = auth.currentUser?.uid;
    
    if (!token || !uid) {
        throw new Error('User not authenticated');
    }
    
    return {
        'auth-token': `Bearer ${token}`,
        'Authorization': `${token}`,
        'requesting-user-uid': uid,
    };
};

/**
 * Document Markup API
 */
export const documentMarkupApi = {
    /**
     * List all user's document markups
     */
    async listDocuments() {
        const headers = await getAuthHeaders();
        
        const response = await fetch(`${API_BASE}/document-markups/`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                ...headers
            }
        });
        
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to fetch documents');
        }
        
        return response.json();
    },
    
    /**
     * Upload a new document for markup
     */
    async uploadDocument(file) {
        const headers = await getAuthHeaders();
        
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await fetch(`${API_BASE}/document-markups/upload/`, {
            method: 'POST',
            headers: headers, // Don't set Content-Type for FormData
            body: formData
        });
        
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to upload document');
        }
        
        return response.json();
    },
    
    /**
     * Get document with file data
     */
    async getDocument(documentId) {
        const headers = await getAuthHeaders();
        
        const response = await fetch(`${API_BASE}/document-markups/${documentId}/`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                ...headers
            }
        });
        
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to fetch document');
        }
        
        return response.json();
    },
    
    /**
     * Delete a document
     */
    async deleteDocument(documentId) {
        const headers = await getAuthHeaders();
        
        const response = await fetch(`${API_BASE}/document-markups/${documentId}/`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                ...headers
            }
        });
        
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to delete document');
        }
        
        return response.json();
    },
    
    /**
     * Get strokes for a document (optionally filtered by page)
     */
    async getStrokes(documentId, pageNumber = null) {
        const headers = await getAuthHeaders();
        
        const url = pageNumber !== null 
            ? `${API_BASE}/document-markups/${documentId}/strokes/?page_number=${pageNumber}`
            : `${API_BASE}/document-markups/${documentId}/strokes/`;
        
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                ...headers
            }
        });
        
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to fetch strokes');
        }
        
        return response.json();
    },
    
    /**
     * Save strokes (batch) for a specific page
     */
    async saveStrokes(documentId, strokes, pageNumber = 1) {
        const headers = await getAuthHeaders();
        
        const response = await fetch(`${API_BASE}/document-markups/${documentId}/strokes/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...headers
            },
            body: JSON.stringify({ 
                strokes,
                page_number: pageNumber
            })
        });
        
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to save strokes');
        }
        
        return response.json();
    }
};
